import { AppShell } from './AppShell';
import type { NavGroup, NavTabs } from './nav-types';

/**
 * Eighteen destinations, grouped by the question the teacher is asking rather
 * than by the table they live in: who is enrolled, what is being taught, how it
 * is being assessed, what it costs and earns, who runs the place.
 */
const GROUPS: readonly NavGroup[] = [
  {
    label: 'Overview',
    items: [
      { to: '/institute/dashboard', label: 'Dashboard', icon: 'dashboard' },
      // The queue as a page (phase 174): what to do today, with the actions
      // to do it. The dashboard keeps an eight-row preview that links here.
      { to: '/institute/work', label: 'Work', icon: 'work' },
      // One row, one number: it carries the unread count and it is where the
      // count goes when you press it.
      {
        to: '/institute/notifications',
        label: 'Notifications',
        icon: 'notifications',
        badge: 'notifications',
        matchSubRoutes: true,
      },
    ],
  },
  {
    label: 'Students',
    items: [
      { to: '/institute/students', label: 'Students', icon: 'students', matchSubRoutes: true },
      { to: '/institute/messages', label: 'Messages', icon: 'messages', matchSubRoutes: true },
      { to: '/institute/workflow', label: 'Workflow', icon: 'workflow', matchSubRoutes: true },
      { to: '/institute/batches', label: 'Batches', icon: 'batches', matchSubRoutes: true },
      { to: '/institute/attendance', label: 'Attendance', icon: 'attendance' },
    ],
  },
  {
    label: 'Teaching',
    items: [
      { to: '/institute/sessions', label: 'Sessions', icon: 'sessions', matchSubRoutes: true },
      {
        to: '/institute/assignments',
        label: 'Assignments',
        icon: 'assignment',
        matchSubRoutes: true,
      },
    ],
  },
  {
    label: 'Assessment',
    items: [
      { to: '/institute/grading', label: 'Grading', icon: 'grading', matchSubRoutes: true },
      { to: '/institute/results', label: 'Results', icon: 'results', matchSubRoutes: true },
      { to: '/institute/tests', label: 'Tests', icon: 'tests' },
      {
        to: '/institute/practice-sets',
        label: 'Practice sets',
        icon: 'practiceSets',
        matchSubRoutes: true,
      },
      // The per-skill module pages under /institute/builder/* are the main
      // flow, so keep this item lit while the teacher is inside one.
      { to: '/institute/builder', label: 'Test builder', icon: 'builder', matchSubRoutes: true },
    ],
  },
  {
    label: 'Money',
    items: [
      // matchSubRoutes because every finance screen lives under this path and
      // the section has its own second-level tabs.
      { to: '/institute/finance', label: 'Finance', icon: 'finance', matchSubRoutes: true },
    ],
  },
  {
    label: 'Institute',
    items: [
      { to: '/institute/staff', label: 'Staff', icon: 'staff' },
      { to: '/institute/settings', label: 'Settings', icon: 'settings' },
    ],
  },
  // One row per workspace, in the same slot, because the account block in the
  // sidebar foot is easy to miss on a phone where the drawer scrolls.
  {
    label: 'Account',
    items: [{ to: '/institute/profile', label: 'Profile', icon: 'account' }],
  },
];

/** What a teacher reaches for between classes, on a phone. */
const TABS: NavTabs = [
  { to: '/institute/dashboard', label: 'Dashboard', icon: 'dashboard' },
  { to: '/institute/students', label: 'Students', icon: 'students', matchSubRoutes: true },
  { to: '/institute/grading', label: 'Grading', icon: 'grading', matchSubRoutes: true },
  { to: '/institute/results', label: 'Results', icon: 'results', matchSubRoutes: true },
];

export function InstituteLayout(): JSX.Element {
  return (
    <AppShell
      brand="IELTS Mock"
      workspace="Institute"
      profileHref="/institute/profile"
      homeHref="/institute/dashboard"
      groups={GROUPS}
      tabs={TABS}
    />
  );
}
